import { createSelector } from 'reselect';
import { getIsApmERPJobFlow } from './getIsApmERPJobFlow';
import { getProgramEvents } from './getProgramEvents';
import { getIsSetupModeActive } from './getIsSetupModeActive';
import { getOpenLaborTicketRefs } from './getOpenLaborTicketRefs';
import { getCurrentWorkOrderOpRef } from './getCurrentWorkOrderOpRef';

/**
 * Returns the active order quantity for an APM ERP job flow machine, derived
 * from the visible program event for the current job's operation, or the most
 * recent visible event with an order quantity when there's no current job. A
 * current job with no such event (a labor ticket waiting for its program, or
 * one with no operation at all) gets null rather than another operation's
 * quantities.
 *
 * Events with a closedDate or no order quantity are skipped. The current job
 * can be any other event, as useProgramEventDisplayState counts every group the
 * server sends, including a confirmed run whose labor ticket has closed. With no
 * current job, the newest event also skips confirmed events whose labor ticket
 * is no longer open, so a finished run doesn't stand in for current work.
 *
 * Returns null for non-APM flows, during setup mode, and when no active event exists.
 *
 * goodQuantity is the good parts made: submitted good parts plus parts counted since, less parts
 * rejected from good that no closed ticket has submitted yet. It falls back to completedQuantity
 * from a Dashboard without it. With it, unsubmittedGoodParts is the good parts no ticket has
 * submitted yet; uncoveredParts would also count those rejected-from-good parts, which are in
 * rejectedParts.
 *
 * operationGoodQuantity is the good parts toward the work order operation across every machine on
 * it, submitted or not, and operationMachineCount how many machines those come from; both null
 * when the Dashboard didn't send them.
 */
export const getApmCurrentOrderQuantity = createSelector(
  [
    getIsApmERPJobFlow,
    getProgramEvents,
    getIsSetupModeActive,
    getOpenLaborTicketRefs,
    getCurrentWorkOrderOpRef,
  ],
  (
    isApmERPJobFlow,
    programEvents,
    isSetupModeActive,
    openLaborTicketRefs,
    currentWooRef
  ) => {
    if (!isApmERPJobFlow || isSetupModeActive) {
      return null;
    }

    const openLtRefSet = new Set(openLaborTicketRefs);

    const activeEvents = programEvents
      .filter((event) => {
        return !event.closedDate && event.orderQuantity != null;
      })
      .slice()
      .sort((a, b) => {
        const aTime = a.eventTime ? new Date(a.eventTime).getTime() : 0;
        const bTime = b.eventTime ? new Date(b.eventTime).getTime() : 0;
        return bTime - aTime;
      });

    // undefined: no current job. null: a current job with no operation, which has no event.
    const activeEvent =
      currentWooRef === undefined
        ? activeEvents.find((event) => {
            return (
              !event.confirmedAt ||
              !event.laborTicketRef ||
              openLtRefSet.has(event.laborTicketRef)
            );
          })
        : activeEvents.find((event) => {
            return (
              currentWooRef != null &&
              String(event.workOrderOperationRef) === String(currentWooRef)
            );
          });
    if (!activeEvent) {
      return null;
    }

    const { orderQuantity, workOrderOperationRef } = activeEvent;
    const completedQuantity = orderQuantity?.completedQuantity ?? 0;
    const submittedBadParts = orderQuantity?.submittedBadParts ?? 0;
    const liveGoodQuantity = orderQuantity?.goodQuantity;

    return {
      workOrderOperationRef:
        workOrderOperationRef != null ? String(workOrderOperationRef) : null,
      completedQuantity,
      finishQuantity: orderQuantity?.finishQuantity ?? null,
      uncoveredParts: orderQuantity?.uncoveredParts ?? null,
      submittedBadParts,
      goodQuantity: liveGoodQuantity ?? completedQuantity,
      rejectedParts:
        submittedBadParts + (orderQuantity?.unsubmittedRejectedParts ?? 0),
      unsubmittedGoodParts:
        liveGoodQuantity != null
          ? liveGoodQuantity - (orderQuantity?.submittedGoodParts ?? 0)
          : null,
      operationGoodQuantity: orderQuantity?.operationGoodQuantity ?? null,
      operationMachineCount: orderQuantity?.operationMachineCount ?? null,
    };
  }
);
